import { useEffect, useState } from 'react'
import { Link, useNavigate, useSearchParams } from 'react-router-dom'
import { Button } from '@/components/ui/button'
import { Check, AlertTriangle, Loader2, Mail, Shield } from 'lucide-react'
import { useQueryClient } from '@tanstack/react-query'
import { useAuth } from '@/contexts/AuthContext'
import { useToast } from '@/hooks/use-toast'
import { fetchTeamInvite, acceptTeamInvite } from '@/lib/notificationWorkerClient'

interface InviteDetails {
  organizationId: string
  email: string
  role: 'owner' | 'admin' | 'manager' | 'viewer'
  expiresAt: string
}

export default function TeamAcceptPage() {
  const [params] = useSearchParams()
  const token = params.get('token') ?? ''
  const navigate = useNavigate()
  const { toast } = useToast()
  const { user } = useAuth()
  const qc = useQueryClient()

  const [invite, setInvite] = useState<InviteDetails | null>(null)
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState('')
  const [accepting, setAccepting] = useState(false)
  const [accepted, setAccepted] = useState(false)

  useEffect(() => {
    if (!token) {
      setError('No invitation token provided.')
      setLoading(false)
      return
    }
    fetchTeamInvite(token)
      .then(setInvite)
      .catch((err) =>
        setError(err instanceof Error ? err.message : 'Failed to load invitation'),
      )
      .finally(() => setLoading(false))
  }, [token])

  const handleAccept = async () => {
    setAccepting(true)
    try {
      await acceptTeamInvite(token)
      // Refresh any team-scoped queries on the next page.
      void qc.invalidateQueries()
      setAccepted(true)
      toast({ title: 'Invitation accepted', description: 'Welcome to the team!' })
      setTimeout(() => navigate('/dashboard'), 1500)
    } catch (err) {
      toast({
        variant: 'destructive',
        title: 'Failed to accept invitation',
        description: err instanceof Error ? err.message : 'An error occurred.',
      })
    } finally {
      setAccepting(false)
    }
  }

  if (loading) {
    return (
      <div className="flex justify-center items-center min-h-[40vh]">
        <Loader2 className="h-6 w-6 animate-spin text-muted-foreground" />
      </div>
    )
  }

  if (error || !invite) {
    return (
      <div className="space-y-8 max-w-md mx-auto py-12">
        <div className="bg-destructive/10 border border-destructive/20 rounded-lg p-8 text-center">
          <AlertTriangle className="h-12 w-12 text-destructive mx-auto" />
          <h2 className="mt-4 text-xl font-semibold text-destructive">Invalid Invitation</h2>
          <p className="mt-2 text-muted-foreground">
            {error || 'This invitation could not be loaded.'}
          </p>
          <Button asChild className="mt-4">
            <Link to="/dashboard">Go to Dashboard</Link>
          </Button>
        </div>
      </div>
    )
  }

  const expired = new Date(invite.expiresAt) < new Date()
  if (expired) {
    return (
      <div className="space-y-8 max-w-md mx-auto py-12">
        <div className="bg-destructive/10 border border-destructive/20 rounded-lg p-8 text-center">
          <AlertTriangle className="h-12 w-12 text-destructive mx-auto" />
          <h2 className="mt-4 text-xl font-semibold text-destructive">Invitation Expired</h2>
          <p className="mt-2 text-muted-foreground">
            This invitation expired on {new Date(invite.expiresAt).toLocaleDateString()}.
            Ask the inviter to send a new one.
          </p>
        </div>
      </div>
    )
  }

  if (accepted) {
    return (
      <div className="space-y-8 max-w-md mx-auto py-12">
        <div className="bg-card text-card-foreground rounded-lg border p-12 text-center">
          <div className="h-16 w-16 bg-secondary/15 rounded-full flex items-center justify-center mx-auto">
            <Check className="h-8 w-8 text-secondary" />
          </div>
          <h2 className="mt-6 text-2xl font-bold">Welcome to the team!</h2>
          <p className="mt-4 text-muted-foreground">Redirecting to your dashboard…</p>
        </div>
      </div>
    )
  }

  const emailMismatch =
    user?.email && user.email.toLowerCase() !== invite.email.toLowerCase()
  if (emailMismatch) {
    return (
      <div className="space-y-8 max-w-md mx-auto py-12">
        <div className="bg-card text-card-foreground rounded-lg border p-8 text-center">
          <Shield className="h-12 w-12 text-destructive mx-auto" />
          <h2 className="mt-4 text-xl font-semibold">Email Mismatch</h2>
          <p className="mt-2 text-muted-foreground">
            This invitation was sent to <strong>{invite.email}</strong>, but you're signed in as{' '}
            <strong>{user?.email}</strong>. Sign out and sign in with the invited email to accept.
          </p>
        </div>
      </div>
    )
  }

  return (
    <div className="space-y-8 max-w-md mx-auto py-12">
      <div className="bg-card text-card-foreground rounded-lg border p-8 text-center">
        <Mail className="h-12 w-12 text-primary mx-auto" />
        <h2 className="mt-4 text-xl font-semibold">You've been invited</h2>
        <p className="mt-2 text-muted-foreground">
          You've been invited to join as a <strong>{invite.role}</strong>.
        </p>
        <p className="mt-1 text-xs text-muted-foreground">
          Expires {new Date(invite.expiresAt).toLocaleDateString()}.
        </p>
        <Button onClick={handleAccept} disabled={accepting} className="mt-6 w-full">
          {accepting ? <Loader2 className="mr-2 h-4 w-4 animate-spin" /> : null}
          {accepting ? 'Accepting…' : 'Accept Invitation'}
        </Button>
      </div>
    </div>
  )
}
